import { textoDeEstado } from "../services/cols/estado";
import { etiquetaDeFicha } from "./empresa";

// Lógica pura de "personas", sin Firestore ni Express ni React.
// La usan tanto el back (con datos recién leídos de Firestore) como el front
// (con datos que ya tiene en memoria por el listener en tiempo real),
// para no tener la misma lógica escrita (y desincronizada) en dos lugares.

/* EL MISMO CRITERIO QUE TRACTORES, FURGONES Y VEHÍCULOS, que a personas nunca
 * se le había aplicado — y por eso la pantalla mostraba "true" y "false" en la
 * columna Estado y en el filtro.
 *
 * Lo de antes tenía el bug que `estado.js` documenta: `estado || …` es
 * verdadero para el STRING "false", que no es vacío. Todo lo dado de baja
 * aparecía como Activo, y lo que no tenía nada cargado aparecía en blanco —
 * indistinguible de un error de carga.
 *
 * Son TRES estados y no dos. "Sin dato" existe de verdad y es la lista de lo
 * que hay que ir a completar: meterlo en cualquiera de los otros dos es
 * inventar. */
export function calcularEstadoReal(persona) {
  return textoDeEstado(persona?.estado);
}

export function enriquecerPersona(persona, empresasPorId = {}) {
  const empresa = empresasPorId[String(persona.empresa)];
  const tienePuestoYEspecializacion = persona.puesto && persona.especializacion;

  return {
    ...persona,
    nombreCompleto: `${persona.apellido}, ${persona.nombres}`.trim(),
    nombreEmpresa: etiquetaDeFicha(persona.empresa, empresa),
    puestoCompleto: tienePuestoYEspecializacion
      ? `${persona.puesto} (${persona.especializacion})`
      : persona.puesto || "",
    estadoReal: calcularEstadoReal(persona),
  };
}

export function enriquecerPersonas(personas, empresas = []) {
  const empresasPorId = {};
  empresas.forEach((e) => {
    empresasPorId[e.id] = e;
  });
  return personas.map((p) => enriquecerPersona(p, empresasPorId));
}

export function filtrarPorPuesto(personas, puesto) {
  return personas.filter(
    (p) => (p.puesto || "").toLowerCase() === puesto.toLowerCase()
  );
}

export function filtrarPorEspecializacion(personas, especializacion) {
  return personas.filter(
    (p) =>
      (p.especializacion || "").toLowerCase() === especializacion.toLowerCase()
  );
}

export const getPersonaPorId = (personas, id) =>
  personas.find((p) => String(p.id) === String(id));

export const getChoferes = (personas) => filtrarPorPuesto(personas, "chofer");
export const getChoferesLarga = (personas) =>
  filtrarPorEspecializacion(personas, "larga distancia");
export const getChoferesMovimiento = (personas) =>
  filtrarPorEspecializacion(personas, "movimiento");
export const getChoferesFleteros = (personas) =>
  filtrarPorEspecializacion(personas, "fletero");
export const getSerenos = (personas) =>
  filtrarPorEspecializacion(personas, "sereno");

// ⚠️ OJO: estos dos quedaron DEFINIDOS DISTINTO en el back y en el front
// original (por eso probablemente daban resultados distintos en cada lado).
// Dejo acá el comportamiento que tenía el BACK (el que está en producción),
// marcado para que se confirme cuál es el correcto:
//   - back  (actual):  Mecanicos      = puesto === "taller"            (incluye plomeros, etc)
//   - front (actual):  Mecanicos      = especializacion === "mecanico" (solo mecánicos)
//   - back  (actual):  Administrativos = puesto === "administrativo"
//   - front (actual):  Administrativos = especializacion === "administrativo"
// TODO: confirmar cuál de las dos definiciones es la correcta para cada caso.
export const getMecanicos = (personas) => filtrarPorPuesto(personas, "taller");
export const getAdministrativos = (personas) =>
  filtrarPorPuesto(personas, "administrativo");

/* La ruta de la ficha de una persona.
 *
 * Vive acá, junto al resto de lo que se sabe de una persona, para que ningún
 * componente arme la dirección a mano: el día que cambie, cambia en un lugar.
 * Es el gemelo de `unidad.ruta`.
 *
 * VA POR DNI y no por el id de la fila: el DNI es lo que escribe portería en
 * cada evento y lo que cruza con la planilla de documentación, así que es la
 * clave con la que esta persona se puede seguir por todo el sistema. Sin DNI
 * no hay ficha —hay filas del padrón sin él— y devolver una ruta igual sería
 * un enlace a una pantalla que no puede consultar nada.
 */
export function rutaDePersona(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  return d ? `/persona/${d}` : null;
}

/* Cómo se lo nombra: «APELLIDO, NOMBRES».
 *
 * Se arma con las dos columnas y no con `nombre`, que está en 2.202 de 2.476:
 * usar la incompleta dejaría 274 personas sin nombre en su propia ficha. */
export function nombreDePersona(p) {
  const armado = [p?.apellido, p?.nombres].filter(Boolean).join(", ");
  return armado || p?.nombre || null;
}
